import { createTheme, responsiveFontSizes } from "@material-ui/core";
import SuisseBPIntlBold from "./fonts/SuisseBPIntlBold.woff2";

export const COLORS = {
  blue: "#1975e6",
  blueWithTransparency: "rgba(25, 117, 230, 0.8)",
  gray: "#4e4e54",
  green: "#0ac2af",
  greenWithTransparency: "rgba(10, 194, 175, 0.8)",
  lightGreen: "rgba(51, 242, 223, 1)",
  lightBlue: "#83b9fc",
  nearBlack: "hsl(240, 100%, 16%)",
  primaryDark: "hsla(210, 5%, 8%, .96)",
  // nearBlack: "var(--color-primary-500)",
  nearBlackWithMinorTransparency: "rgba(0,0,0,.25)",
  red: "#aa0818",
  darkRed: "#810612",
  white: "#FFFFFF",
  whiteWithTransparency: "rgba(255,255,255,.07)",
};

const suisse = {
  fontFamily: "Suisse BP Intl",
  fontStyle: "normal",
  fontDisplay: "swap",
  fontWeight: 400,
  src: `url(${SuisseBPIntlBold}) format('woff2')`,
};

export const theme = responsiveFontSizes(
  createTheme({
    palette: {
      type: "dark",
      background: {
        default: COLORS.nearBlack,
        paper: 'var(--color-primary-100)',
      },
      divider: COLORS.white,
      text: {
        primary: COLORS.primaryDark,
        secondary: COLORS.gray,
      },
      primary: {
        main: COLORS.blueWithTransparency, // #0074FF
        light: COLORS.lightBlue,
      },
      secondary: {
        main: COLORS.greenWithTransparency, // #00EFD8
        light: COLORS.lightGreen,
      },
      error: {
        main: COLORS.red,
      },
    },

    typography: {
      fontFamily: "var(--font-family-primary)",
      fontSize: 13,
      h1: {
        fontFamily: "var(--font-family-secondary)",
        lineHeight: 0.9,
        letterSpacing: -2,
        fontWeight: "bold",
      },
      h2: {
        fontWeight: "200",
      },
      h4: {
        fontWeight: "600",
        fontFamily: "var(--font-family-secondary)",
        letterSpacing: -1.02,
      },
    },
    overrides: {
      MuiCssBaseline: {
        "@global": {
          "@font-face": [suisse],
          body: {
            overscrollBehaviorY: "none",
            backgroundPosition: "top center",
            backgroundRepeat: "repeat-y",
            backgroundSize: "120%",
          },
          "*": {
            scrollbarWidth: "thin",
            scrollbarColor: `${COLORS.gray} ${COLORS.nearBlackWithMinorTransparency}`,
          },
          "*::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
            backgroundColor: COLORS.nearBlackWithMinorTransparency,
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: COLORS.gray,
            borderRadius: "4px",
          },
          "*::-webkit-scrollbar-corner": {
            // this hides an annoying white box which appears when both scrollbars are present
            backgroundColor: "transparent",
          },
        },
      },
      MuiAccordion: {
        root: {
          backgroundColor: COLORS.whiteWithTransparency,
          "&:before": {
            display: "none",
          },
        },
        rounded: {
          "&:first-child": {
            borderTopLeftRadius: "28px",
            borderTopRightRadius: "28px",
          },
          "&:last-child": {
            borderBottomLeftRadius: "28px",
            borderBottomRightRadius: "28px",
          },
        },
      },
      MuiAlert: {
        root: {
          borderRadius: "8px",
          border: "1px solid",
        },
        outlinedWarning: {
          color: 'var(--color-warning-800)',
        },
        outlinedInfo: {
          color: 'var(--color-primary-400)',
        }
      },
      MuiSelect: {
        icon: {
          color: COLORS.primaryDark,
        },
      },
      MuiIconButton: {
        root: {
          color: COLORS.primaryDark,
        },
      },
      MuiButton: {
        root: {
          color: COLORS.primaryDark,
          borderRadius: "22px",
          letterSpacing: ".1em",
        },
        outlinedSizeSmall: {
          padding: "6px 9px",
          fontSize: "0.70rem",
        },
        outlined: {
          borderColor: 'hsl(216deg,17%,88%)',
        }
      },
      MuiLink: {
        root: {
          color: COLORS.lightBlue,
        },
      },
      MuiPaper: {
        root: {
          color: COLORS.primaryDark,
          backgroundColor: "var(--color-primary-100)",
        },
        rounded: {
          borderRadius: "28px",
          backdropFilter: "blur(4px)",
        },
      },
      Mui: {
        "&$disabled": {
          color: "var(--color-gray-500)",
        }
      },
      MuiInputBase: {
        root: {
          color: COLORS.primaryDark,
        },
      },
      MuiStepper: {
        root: {
          backgroundColor: "transparent",
          padding: 0,
        },
      },
      MuiOutlinedInput: {
        notchedOutline: {
          borderColor: "hsl(216deg,17%,88%)",
        },
        root: {
          "&:hover": {
            borderColor: "hsl(216deg,17%,88%)",
          },
        },
      },
      MuiPopover: {
        paper: {
          background: "var(--color-primary-100)",
        },
      },
      MuiListItem: {
        button: {
          "&:hover": {
            backgroundColor: "var(--color-primary-200)",
          }
        }
      },
      MuiStep: {
        root: {
          // backgroundColor: COLORS.whiteWithTransparency,
          backgroundColor: COLORS.white,
          backdropFilter: "blur(4px)",
          borderRadius: "28px",
          padding: "32px 32px 16px",
        },
      },
      MuiStepConnector: {
        lineVertical: {
          borderLeftWidth: 0,
        },
      },
      MuiStepContent: {
        root: {
          borderLeftWidth: 0,
          marginLeft: 0,
          paddingLeft: 0,
        },
      },
      MuiStepLabel: {
        label: {
          color: COLORS.primaryDark,
          textTransform: "uppercase",
          "&.MuiStepLabel-active": {
            color: COLORS.primaryDark,
          },
          "&.MuiStepLabel-completed": {},
        },
      },
      MuiTabs: {
        root: {
          borderBottom: `1px solid ${COLORS.primaryDark}`,
        },
        indicator: {
          height: "100%",
          background: "linear-gradient(20deg, #4180dc 0%, #a993f9 100%);",
          zIndex: -1,
          borderRadius: 8,
        },
      },
      MuiTab: {
        root: {
          color: COLORS.primaryDark,
          fontFamily: "Suisse BP Intl, sans-serif",
          fontWeight: "bold",
          fontSize: 18,
          padding: 12,
          letterSpacing: "-0.69px",
          textTransform: "none",
        },
        textColorInherit: {
          opacity: 1,
        },
      },
      MuiTableCell: {
        root: {
          borderBottom: "none",
        },
      },
    },
  })
);
